import { useState, useActionState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '../stores/auth'
import { LegalFooter } from '../components/LegalFooter'
import { api } from '../services/api'
import { Icon } from '../components/common'
import { homePath, landingPath } from '../utils/navigation'
import type { LoginResponse, LoginChallenge, RegisterResponse, CloudUser, CloudTenant } from '../types'

type FormState = {
  error: string | null
}

type TOTPEnrollment = {
  secret: string
  otpauth_uri: string
}

const initialState: FormState = { error: null }

export function Login() {
  const [mode, setMode] = useState<'login' | 'register'>('login')
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { user, setTokens, setUser } = useAuthStore()
  const [totpEnrollment, setTotpEnrollment] = useState<TOTPEnrollment | null>(null)
  // Set when the password matched and a sign-in code was emailed.
  const [challenge, setChallenge] = useState<LoginChallenge | null>(null)

  const requestedReturn = searchParams.get('return')
  const reason = searchParams.get('reason')

  // Redirect if already authenticated
  useEffect(() => {
    if (user) {
      navigate(landingPath(user.role, requestedReturn), { replace: true })
    }
  }, [user, navigate, requestedReturn])

  // Login action
  const [loginState, loginAction, loginPending] = useActionState(
    async (_prev: FormState, formData: FormData): Promise<FormState> => {
      const email = (formData.get('email') as string)?.trim()
      const password = formData.get('password') as string
      const tenantSlug = (formData.get('tenant_slug') as string)?.trim()
      const mfaCode = (formData.get('mfa_code') as string)?.trim()

      if (!email || !password) {
        return { error: 'Please fill in all fields' }
      }

      try {
        const res = await api.post<LoginResponse | LoginChallenge>('/auth/login', {
          email,
          password,
          tenant_slug: tenantSlug || undefined,
          mfa_code: mfaCode || undefined,
        })
        if ('challenge_id' in res) {
          setChallenge(res)
          return { error: null }
        }

        setTokens(res.access_token, res.refresh_token)

        // Fetch full user profile + tenant
        let signedIn = res.user
        try {
          const meRes = await api.get<{ user: CloudUser; tenant: CloudTenant }>('/me')
          signedIn = meRes.user
          setUser(meRes.user, meRes.tenant)
        } catch {
          // Login response has the user, use it as fallback
          setUser(res.user)
        }

        navigate(landingPath(signedIn.role, requestedReturn), { replace: true })
        return { error: null }
      } catch (err) {
		if (err && typeof err === 'object' && 'status' in err && (err as { status: number }).status === 428) {
			try {
				const enrollment = await api.post<TOTPEnrollment>('/auth/mfa/totp/enroll', { email, password })
				setTotpEnrollment(enrollment)
				return { error: 'Add the setup key to your authenticator, then enter its six-digit code.' }
			} catch (enrollmentError) {
				if (enrollmentError && typeof enrollmentError === 'object' && 'message' in enrollmentError) {
					return { error: (enrollmentError as { message: string }).message }
				}
				return { error: 'Could not start MFA enrollment. Please try again.' }
			}
		}
        if (err && typeof err === 'object' && 'status' in err && (err as { status: number }).status === 429) {
          return { error: 'Too many login attempts. Please wait a minute.' }
        }
        if (err && typeof err === 'object' && 'message' in err) {
          return { error: (err as { message: string }).message }
        }
        return { error: 'Login failed. Please try again.' }
      }
    },
    initialState
  )

  // Register action
  const [registerState, registerAction, registerPending] = useActionState(
    async (_prev: FormState, formData: FormData): Promise<FormState> => {
      const tenantName = (formData.get('tenant_name') as string)?.trim()
      const email = (formData.get('email') as string)?.trim()
      const password = formData.get('password') as string
      const confirmPassword = formData.get('confirm_password') as string

      if (!tenantName || !email || !password || !confirmPassword) {
        return { error: 'Please fill in all fields' }
      }

      if (password.length < 8) {
        return { error: 'Password must be at least 8 characters' }
      }

      if (password !== confirmPassword) {
        return { error: 'Passwords do not match' }
      }

      try {
        const res = await api.post<RegisterResponse>('/auth/register', {
          tenant_name: tenantName,
          email,
          password,
        })

        setTokens(res.access_token, res.refresh_token)
        setUser(res.user, res.tenant)

        navigate(homePath(res.user.role), { replace: true })
        return { error: null }
      } catch (err) {
        if (err && typeof err === 'object' && 'status' in err && (err as { status: number }).status === 429) {
          return { error: 'Too many attempts. Please wait a minute.' }
        }
        if (err && typeof err === 'object' && 'message' in err) {
          return { error: (err as { message: string }).message }
        }
        return { error: 'Registration failed. Please try again.' }
      }
    },
    initialState
  )

  // Sign-in code action (second step of an email-verified sign-in)
  const [codeState, codeAction, codePending] = useActionState(
    async (_prev: FormState, formData: FormData): Promise<FormState> => {
      const code = (formData.get('code') as string)?.trim()
      if (!challenge || !/^\d{6}$/.test(code)) {
        return { error: 'Enter the six-digit code from your email' }
      }
      try {
        const res = await api.post<LoginResponse>('/auth/login/mfa', { challenge_id: challenge.challenge_id, code })
        setTokens(res.access_token, res.refresh_token)
        let signedIn = res.user
        try {
          const meRes = await api.get<{ user: CloudUser; tenant: CloudTenant }>('/me')
          signedIn = meRes.user
          setUser(meRes.user, meRes.tenant)
        } catch {
          setUser(res.user)
        }
        navigate(landingPath(signedIn.role, requestedReturn), { replace: true })
        return { error: null }
      } catch (err) {
        if (err && typeof err === 'object' && 'message' in err) {
          return { error: (err as { message: string }).message }
        }
        return { error: 'That code was not accepted. Sign in again for a new one.' }
      }
    },
    initialState
  )

  const isPending = challenge ? codePending : mode === 'login' ? loginPending : registerPending
  const error = challenge ? codeState.error : mode === 'login' ? loginState.error : registerState.error

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-header">
          <div className="login-logo">
            <Icon name="cloud" size={28} />
          </div>
          <p className="login-title">Sentyr Cloud</p>
          <p className="login-subtitle">Cloud Access Control</p>
        </div>

        <h1 className="login-heading">
          {mode === 'login' ? 'Sign in' : 'Create account'}
        </h1>

        {reason === 'session_expired' && (
          <div className="login-info show" role="status">
            Your session ended. Sign in again.
          </div>
        )}

        {error && (
          <div className="login-error show" role="alert">
            {error}
          </div>
        )}

        {challenge ? (
          <form className="login-form" action={codeAction}>
            <div className="login-info show" role="status">
              We emailed you a six-digit sign-in code. It expires in {Math.round(challenge.expires_in / 60)} minutes.
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="login-code">Sign-in code</label>
              <input
                type="text"
                id="login-code"
                name="code"
                className="form-input"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                autoFocus
                disabled={isPending}
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={isPending}>
              {isPending ? <span className="loading-spinner sm" /> : 'Verify code'}
            </button>
            <button type="button" className="btn btn-secondary" style={{ marginTop: '8px' }} onClick={() => setChallenge(null)}>
              Start over
            </button>
          </form>
        ) : mode === 'login' ? (
          <form className="login-form" action={loginAction}>
            <div className="form-group">
              <label className="form-label" htmlFor="login-email">
                Email
              </label>
              <input
                type="email"
                id="login-email"
                name="email"
                className="form-input"
                required
                autoFocus
                autoComplete="email"
                disabled={isPending}
              />
            </div>

            {totpEnrollment && (
              <div className="login-info show" role="status">
                <p>Add this one-time setup key to your authenticator app:</p>
                <code>{totpEnrollment.secret}</code>
              </div>
            )}

            <div className="form-group">
              <label className="form-label" htmlFor="login-mfa-code">
                Authenticator code
              </label>
              <input
                type="text"
                id="login-mfa-code"
                name="mfa_code"
                className="form-input"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                disabled={isPending}
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="login-tenant-slug">
                Workspace (optional)
              </label>
              <input
                type="text"
                id="login-tenant-slug"
                name="tenant_slug"
                className="form-input"
                autoComplete="organization"
                disabled={isPending}
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="login-password">
                Password
              </label>
              <input
                type="password"
                id="login-password"
                name="password"
                className="form-input"
                required
                autoComplete="current-password"
                disabled={isPending}
              />
            </div>

            <button type="submit" className="btn btn-primary" disabled={isPending}>
              {isPending ? <span className="loading-spinner sm" /> : 'Sign in'}
            </button>
            <div style={{ textAlign: 'center', marginTop: '12px', fontSize: 'var(--text-sm)' }}>
              <Link to="/forgot">Forgot password?</Link>
            </div>
          </form>
        ) : (
          <form className="login-form" action={registerAction}>
            <div className="form-group">
              <label className="form-label" htmlFor="register-tenant">
                Organization Name
              </label>
              <input
                type="text"
                id="register-tenant"
                name="tenant_name"
                className="form-input"
                required
                autoFocus
                autoComplete="organization"
                disabled={isPending}
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="register-email">
                Email
              </label>
              <input
                type="email"
                id="register-email"
                name="email"
                className="form-input"
                required
                autoComplete="email"
                disabled={isPending}
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="register-password">
                Password
              </label>
              <input
                type="password"
                id="register-password"
                name="password"
                className="form-input"
                required
                minLength={8}
                autoComplete="new-password"
                disabled={isPending}
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="register-confirm">
                Confirm Password
              </label>
              <input
                type="password"
                id="register-confirm"
                name="confirm_password"
                className="form-input"
                required
                minLength={8}
                autoComplete="new-password"
                disabled={isPending}
              />
            </div>

            <button type="submit" className="btn btn-primary" disabled={isPending}>
              {isPending ? <span className="loading-spinner sm" /> : 'Create account'}
            </button>
          </form>
        )}

        <div style={{
          textAlign: 'center',
          marginTop: 'var(--space-6)',
          fontSize: 'var(--text-sm)',
          color: 'var(--text-tertiary)',
        }}>
          {mode === 'login' ? (
            <>
              Don&apos;t have an account?{' '}
              <button
                type="button"
                onClick={() => setMode('register')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--accent-primary)',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: 'inherit',
                  padding: 0,
                  textDecoration: 'underline',
                }}
              >
                Sign up
              </button>
            </>
          ) : (
            <>
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => setMode('login')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--accent-primary)',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: 'inherit',
                  padding: 0,
                  textDecoration: 'underline',
                }}
              >
                Sign in
              </button>
            </>
          )}
        </div>
      </div>
      <LegalFooter />
    </div>
  )
}
